<!--
    @author bluefirex
-->
<template>
    <div class="acquisit-dev-state-inspector-api default-wrapper">
	    <StateInspectorHeader>
		    <template #default>
			    <h2>API Response</h2>
			    
			    <strong class="flag">
				    {{ backend.label }}
			    </strong>
			    
			    <span class="flag copyable">
				    {{ apiStore.token }}
			    </span>
			    
			    <span class="flag origin">
				    {{ genericStore.origin }}
			    </span>
		    </template>
		    
		    <template #actions>
			    <acquisit-button label="Copy"
			                     :color-container="background"
			                     :disabled="!responses[sectionUid]"
			                     :no-interact="!responses[sectionUid]"
			                     @click="copyResponseToClipboard(sectionUid)" />
		    </template>
	    </StateInspectorHeader>
	    
	    <div class="response"
	         v-if="responses[sectionUid] !== undefined">
		    <StateInspectorJsonView :json="responses[sectionUid]" />
	    </div>
	    
	    <div class="loading"
	         v-else>
		    <acquisit-spinner :color-container="background" size="mini" full-width />
	    </div>
	    
	    <Toast ref="$clipboardToast" content="Copied to clipboard" type="info" />
    </div>
</template>

<script setup lang="ts">
	import { useColor, useColorProps } from '@broker-as/acquisit-ui-vue/helpers'
	import { copyToClipboard } from '@broker-as/acquisit-ui-vue/functions'
	import StateInspectorHeader from '@ui/dev/StateInspectorHeader.vue'
	import { useBackend } from '@/lib/backend-cors'
	import { computed, reactive, ref, watch } from 'vue'
	import StateInspectorJsonView from '@ui/dev/StateInspectorJsonView.vue'
	import { useAPIStore } from '@/stores/api.ts'
	import Toast from '@ui/core/Toast.vue'
	import { useGenericStore } from '@/stores/generic.ts'
	
	const props = defineProps({
		...useColorProps(),
		
		sectionUid: {
			type: String,
			required: false,
			default: null
		}
	})
	
	const color = useColor(props)
	
	const apiStore = useAPIStore()
	const genericStore = useGenericStore()
	const backend = useBackend()
	const responses = reactive<Record<string, any>>({})
	
	const $clipboardToast = ref<typeof Toast|null>()
	
	// Taken from StateInspector.vue: .acquisit-dev-state-inspector
	const background = computed(() => {
		const scssColor = color.parse('#1d212b')
		return scssColor.darken(10)
	})
	
	const copyResponseToClipboard = async (key: string) => {
		if (responses[key] !== undefined) {
			await copyToClipboard(JSON.stringify(responses[key]))
			
			if ($clipboardToast.value) {
				$clipboardToast.value.open(3000)
			} else {
				alert('Copied to clipboard')
			}
		}
	}
	
	const fetchUI = async () => {
		if (responses['api-ui'] !== undefined) {
			return
		}
		
		responses['api-ui'] = await backend.getUI(apiStore.token!)
	}
	
	const fetchOrder = async () => {
		if (responses['api-order'] !== undefined) {
			return
		}
		
		responses['api-order'] = await backend.getOrderByToken(apiStore.token!)
	}
	
	watch(() => props.sectionUid, (section) => {
		switch (section) {
			case 'api-ui':
				fetchUI()
				break
			
			case 'api-order':
				fetchOrder()
				break
		}
	}, {
		immediate: true
	})
</script>

<style lang="scss">
    @use '@/assets/mixins.scss' as m;
    
    .acquisit-dev-state-inspector-api {
    
	    .loading {
		    padding: {
			    top: 64px;
			    bottom: 64px;
		    }
	    }
    }
</style>